import { useTheme } from "@emotion/react";
import { BarTimeChartSynced } from "./bar_time_chart";
import { getBarTimeChartOptions, applyAutoScale } from "./chart_options";

export function FlexChart({ metering, multiDay = false }) {
  const theme = useTheme();

  const utctime = metering.map((el) => new Date(1000 * el.utctime));
  const flex_charge_elion = metering.map((el) => (el.flex_charge_elion ?? 0));
  const flex_charge_peak_shaving = metering.map((el) => (el.flex_charge_peak_shaving ?? 0));
  const flex_discharge_elion = metering.map((el) => -(el.flex_discharge_elion ?? 0));
  const flex_discharge_peak_shaving = metering.map((el) => -(el.flex_discharge_peak_shaving ?? 0));
  const flex_setpoint = metering.map((el) => (el.flex_target ?? 0));
  const hasPeakShavingCharge = flex_charge_peak_shaving.some(
    (value) => Number.isFinite(Number(value)) && Number(value) !== 0,
  );
  const hasPeakShavingDischarge = flex_discharge_peak_shaving.some(
    (value) => Number.isFinite(Number(value)) && Number(value) !== 0,
  );

  const data = {
    labels: utctime,
    datasets: [
      {
        fill: true,
        label: "Flex charge (optimization)",
        data: flex_charge_elion,
        borderWidth: 0,
        borderColor: theme.palette.flex_charge.main,
        backgroundColor: theme.palette.flex_charge.main,
        cubicInterpolationMode: "monotone",
        pointStyle: false,
        pointRadius: 3,
        pointBorderWidth: 1,
        pointBorderColor: theme.palette.flex_charge.main,
        pointHoverRadius: 7,
        hitRadius: 40,
      },
      ...(hasPeakShavingCharge
        ? [{
            fill: true,
            label: "Flex charge (peak shaving)",
            data: flex_charge_peak_shaving,
            borderWidth: 0,
            borderColor: theme.palette.flex_charge.secondary,
            backgroundColor: theme.palette.flex_charge.secondary,
            cubicInterpolationMode: "monotone",
            pointStyle: false,
            pointRadius: 3,
            pointBorderWidth: 1,
            pointBorderColor: theme.palette.flex_charge.secondary,
            pointHoverRadius: 7,
            hitRadius: 40,
          }]
        : []),
      {
        fill: true,
        label: "Flex discharge (optimization)",
        data: flex_discharge_elion,
        borderWidth: 0,
        borderColor: theme.palette.flex_discharge.main,
        backgroundColor: theme.palette.flex_discharge.main,
        cubicInterpolationMode: "monotone",
        pointStyle: false,
        pointRadius: 3,
        pointBorderWidth: 1,
        pointBorderColor: theme.palette.flex_discharge.main,
        pointHoverRadius: 7,
        hitRadius: 40,
      },
      ...(hasPeakShavingDischarge
        ? [{
            fill: true,
            label: "Flex discharge (peak shaving)",
            data: flex_discharge_peak_shaving,
            borderWidth: 0,
            borderColor: theme.palette.flex_discharge.secondary,
            backgroundColor: theme.palette.flex_discharge.secondary,
            cubicInterpolationMode: "monotone",
            pointStyle: false,
            pointRadius: 3,
            pointBorderWidth: 1,
            pointBorderColor: theme.palette.flex_discharge.secondary,
            pointHoverRadius: 7,
            hitRadius: 40,
          }]
        : []),
      {
        fill: false,
        label: "Flex Setpoint",
        data: flex_setpoint,
        borderWidth: 2,
        borderColor: "grey",
        backgroundColor: "transparent",
        cubicInterpolationMode: "monotone",
        pointStyle: false,
        pointRadius: 3,
        pointBorderWidth: 1,
        pointBorderColor: "grey",
        pointHoverRadius: 7,
        hitRadius: 40,
        type: "line",
        order: -1,
      },
    ],
  };

  const options = getBarTimeChartOptions(multiDay);
  applyAutoScale(
    options,
    [
      flex_charge_elion,
      flex_charge_peak_shaving,
      flex_discharge_elion,
      flex_discharge_peak_shaving,
      flex_setpoint,
    ],
    "kW",
  );
  options.plugins = options.plugins || {};
  options.plugins.tooltip = options.plugins.tooltip || {};
  options.plugins.tooltip.filter = (tooltipItem) => {
    const visibleTooltipLabels = [
      "Flex charge (optimization)",
      "Flex discharge (optimization)",
      "Flex Setpoint",
    ];

    if (hasPeakShavingCharge) {
      visibleTooltipLabels.push("Flex charge (peak shaving)");
    }
    if (hasPeakShavingDischarge) {
      visibleTooltipLabels.push("Flex discharge (peak shaving)");
    }

    return visibleTooltipLabels.includes(tooltipItem?.dataset?.label);
  };
  options.plugins.tooltip.callbacks = options.plugins.tooltip.callbacks || {};
  options.plugins.tooltip.callbacks.label = (tooltipItem) => {
    const index = tooltipItem?.dataIndex ?? 0;
    const value = (rawValue, absolute = false) => {
      const numericValue = absolute ? Math.abs(rawValue ?? 0) : (rawValue ?? 0);
      const rounded = Math.round(numericValue * 100) / 100;
      return `${rounded.toLocaleString('de-DE')}`;
    };

    switch (tooltipItem?.dataset?.label) {
      case "Flex charge (optimization)":
        return `Flex charge (optimization): ${value(flex_charge_elion[index])}`;
      case "Flex charge (peak shaving)":
        return `Flex charge (peak shaving): ${value(flex_charge_peak_shaving[index])}`;
      case "Flex discharge (optimization)":
        return `Flex discharge (optimization): ${value(flex_discharge_elion[index], true)}`;
      case "Flex discharge (peak shaving)":
        return `Flex discharge (peak shaving): ${value(flex_discharge_peak_shaving[index], true)}`;
      case "Flex Setpoint":
        return `Flex target: ${value(flex_setpoint[index])}`;
      default:
        return null;
    }
  };
  return <BarTimeChartSynced data={data} options={options} />;
}
